/**
 * O que a página de Downloads mostra de cada pedido (2026-10-15) — puro e
 * testado, pra a tela só desenhar.
 *
 * **O recorte é lido do `criteria` CRU**, no vocabulário do servidor
 * (`DashboardFilters.to_criteria`): é o que ficou congelado no clique, e
 * a página não depende do catálogo vivo pra descrevê-lo. As dimensões
 * guardam o NOME congelado; só marca guarda o id, e o nome dela vem de
 * `criteria_labels.brands`.
 */

import type { ExportRow } from "@/shared/api/api-client";
import {
  APPROACH_CHOICES,
  SOURCE_CHOICES
} from "@/features/mentions/mention-filter-criteria";
import { polarityLabel } from "@/shared/types/polarity";
import { formatCurrencyFull } from "@/shared/utils/format-currency";
import { criteriaChips } from "@/features/reports/ReportCriteriaChips";
import { parseServerTime } from "@/features/uploads/upload-metrics";
import {
  EXPORT_STATUS_LABELS,
  isExportActive,
  type ExportStatus
} from "@/shared/vocabulary/exports";

export type ExportChip = { key: string; label: string };

type Dimension = {
  field: string;
  singular: string;
  plural: string;
  /** Traduz o valor congelado pro texto da tela. */
  valueLabel?: (value: string, row: ExportRow) => string;
};

const choiceLabel =
  (choices: Array<{ value: string; label: string }>) => (value: string) =>
    choices.find(choice => choice.value === value)?.label ?? value;

/** A ordem daqui é a ordem dos chips. */
const DIMENSIONS: Dimension[] = [
  { field: "sentiments", singular: "polaridade", plural: "polaridades", valueLabel: value => polarityLabel(value) },
  { field: "approaches", singular: "abordagem", plural: "abordagens", valueLabel: choiceLabel(APPROACH_CHOICES) },
  { field: "sources", singular: "meio", plural: "meios", valueLabel: choiceLabel(SOURCE_CHOICES) },
  { field: "channels", singular: "canal", plural: "canais" },
  { field: "tiers", singular: "tier", plural: "tiers" },
  { field: "formats", singular: "formato", plural: "formatos" },
  { field: "influencer_types", singular: "porte", plural: "portes" },
  {
    field: "brands",
    singular: "marca",
    plural: "marcas",
    valueLabel: (value, row) => row.criteria_labels?.brands?.[value] ?? `Marca ${value}`
  },
  { field: "themes", singular: "subtema", plural: "subtemas" },
  { field: "theme_groups", singular: "tema", plural: "temas" },
  { field: "publisher_groups", singular: "grupo de publicador", plural: "grupos de publicador" },
  { field: "spokespersons", singular: "porta-voz", plural: "porta-vozes" }
];

/** "2026-03-01" → "01/03/2026", pelos dígitos (data só-dia não tem fuso). */
function dayLabel(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : iso;
}

/** O recorte de leitura do pedido, por extenso. Vazio = a foto inteira. */
export function exportChips(row: ExportRow): ExportChip[] {
  if (row.kind === "export_mentions") {
    // Mesmo formato da `generation_criteria` do relatório (inclusive a
    // janela), então o desenhista é o dele.
    return criteriaChips(
      row.criteria as unknown as Parameters<typeof criteriaChips>[0],
      row.criteria_labels as unknown as Parameters<typeof criteriaChips>[1]
    ).map(({ key, label }) => ({ key, label }));
  }
  const criteria = row.criteria ?? {};
  const chips: ExportChip[] = [];

  for (const dimension of DIMENSIONS) {
    const list = criteria[dimension.field];
    if (!Array.isArray(list) || list.length === 0) continue;
    const values = list.map(value =>
      dimension.valueLabel ? dimension.valueLabel(String(value), row) : String(value)
    );
    const exclude = criteria[`exclude_${dimension.field}`] === true;
    const noun = values.length === 1 ? dimension.singular : dimension.plural;
    chips.push({
      key: dimension.field,
      label: `${exclude ? "Exceto " : ""}${noun}: ${values.join(", ")}`
    });
  }

  const from = typeof criteria.date_from === "string" ? dayLabel(criteria.date_from) : null;
  const to = typeof criteria.date_to === "string" ? dayLabel(criteria.date_to) : null;
  if (from || to) {
    chips.push({
      key: "period",
      // "Publicado": o chip é o filtro da lista, não a janela do
      // relatório (que já está no título) — sem a palavra, "10/09 a 10/09"
      // sob "10 de set. – 15 de set." lia como erro.
      label:
        from && to
          ? from === to
            ? `Publicado em ${from}`
            : `Publicado de ${from} a ${to}`
          : from
            ? `Publicado a partir de ${from}`
            : `Publicado até ${to}`
    });
  }
  if (typeof criteria.min_ad_value === "number") {
    chips.push({
      key: "min_ad_value",
      label: `Valoração ≥ ${formatCurrencyFull(criteria.min_ad_value)}`
    });
  }
  if (criteria.mention_scope === "competitors") {
    chips.push({ key: "mention_scope", label: "Menções dos concorrentes" });
  } else if (criteria.mention_scope === "all") {
    chips.push({ key: "mention_scope", label: "Menções da empresa e dos concorrentes" });
  }

  // Exportação de upload: as linhas pedidas.
  const rows = criteria.only_row_indexes;
  if (Array.isArray(rows) && rows.length > 0) {
    chips.push({
      key: "only_row_indexes",
      label: `${rows.length} ${rows.length === 1 ? "linha escolhida" : "linhas escolhidas"}`
    });
  }
  return chips;
}

/** O estado que a pessoa vê. Um pedido `FINISHED` cujo prazo já passou é
 *  "Expirada" mesmo antes de a faxina do worker carimbar a linha — o
 *  servidor responderia 409 ao baixar, e a tela não deve prometer o
 *  botão. */
export function effectiveStatus(row: ExportRow, nowMs: number = Date.now()): ExportStatus {
  if (row.status === "FINISHED") {
    const expires = parseServerTime(row.expires_at);
    if (expires !== null && expires <= nowMs) return "EXPIRED";
  }
  return row.status;
}

/** O texto de estado, com o motivo quando falhou. */
export function statusText(row: ExportRow, nowMs: number = Date.now()): string {
  const status = effectiveStatus(row, nowMs);
  if (status === "ERRORED" && row.message) {
    return `${EXPORT_STATUS_LABELS.ERRORED}: ${row.message}`;
  }
  return EXPORT_STATUS_LABELS[status];
}

export function canDownloadExport(row: ExportRow, nowMs: number = Date.now()): boolean {
  return effectiveStatus(row, nowMs) === "FINISHED";
}

/** Qualquer pedido que não está andando pode ser repetido; o que anda
 *  já tem o seu botão ("Gerando…"). */
export function canRetryExport(row: ExportRow): boolean {
  return !isExportActive(row.status);
}

/** "22,9 MB" — base 1000 (unidades SI, como o macOS e o servidor
 *  mostram o tamanho). O Explorer do Windows conta em base 1024, então
 *  o mesmo arquivo pode aparecer com número um pouco menor lá. */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return "—";
  if (bytes < 1000) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes;
  let unit = -1;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }
  return `${value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} ${units[unit]}`;
}

/** O que o pedido escolheu de colunas e de cabeçalho, em palavras.
 *  `export_upload_rows` não tem escolha de colunas. */
export function exportLayoutNotes(row: ExportRow): string[] {
  const notes: string[] = [];
  if (row.kind !== "export_upload_rows") {
    notes.push(
      row.columns === null || row.columns === undefined
        ? "Colunas padrão"
        : `${row.columns.length} ${row.columns.length === 1 ? "coluna escolhida" : "colunas escolhidas"}`
    );
  }
  if (row.header === false) notes.push("sem cabeçalho");
  if (row.kind !== "export_upload_rows") {
    if (row.theme_layout === "pair") notes.push("tema e subtemas em duas colunas");
    if (row.highlight_only === true) notes.push("só o subtema em destaque");
  }
  return notes;
}

/** O título da linha. Menções não têm registro-alvo: o título é a janela
 *  do recorte, pelos dígitos (hora de parede, sem `Date`). */
export function exportSubject(row: ExportRow, kindLabel: string): string {
  if (row.kind === "export_mentions") {
    const start = typeof row.criteria?.window_start === "string" ? dayLabel(row.criteria.window_start) : null;
    const end = typeof row.criteria?.window_end === "string" ? dayLabel(row.criteria.window_end) : null;
    if (start && end) return `Menções de ${start} a ${end}`;
    return row.subject_label ?? "Menções do filtro";
  }
  return row.subject_label ?? `${kindLabel} #${row.subject_id}`;
}

/** Para onde o título da linha aponta. */
export function subjectPath(row: ExportRow, companyId: string): string {
  if (row.kind === "export_mentions") return `/empresas/${companyId}/mencoes`;
  if (row.kind === "export_draft") return `/empresas/${companyId}/relatorios/cena?draft=${row.subject_id}`;
  return row.kind === "export_report"
    ? `/empresas/${companyId}/relatorios/cena?report=${row.subject_id}`
    : `/empresas/${companyId}/uploads/${row.subject_id}`;
}
